import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { knowledgeApi } from '../../api/knowledge'
import { useDialog } from '../../components/dialogs/useDialog'
import EmptyState from '../../components/EmptyState'
import Modal from '../../components/Modal'
import { buildTree, flattenVisible, subtreeIds } from './areaTree'
import { AREA_TYPES, consolidationPhaseLabel, type KnowledgeArea } from './knowledgeTypes'

interface ModulesTabProps {
  projectId: number
  canManage: boolean
  selectedAreaId: number | null
  onSelectArea: (id: number | null) => void
  /** Bubbled up so the parent can refresh the drill-down panel's area list. */
  onAreasChanged?: (areas: KnowledgeArea[]) => void
}

type Pending = { kind: 'move' | 'merge'; areaId: number } | null
type AreaDraft = {
  parentId: number | null
  parentName: string | null
}

export function ModulesTab({
  projectId,
  canManage,
  selectedAreaId,
  onSelectArea,
  onAreasChanged,
}: ModulesTabProps) {
  const dialog = useDialog()
  const [areas, setAreas] = useState<KnowledgeArea[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const initializedRef = useRef(false)
  const [pending, setPending] = useState<Pending>(null)
  const [busy, setBusy] = useState(false)
  const [areaDraft, setAreaDraft] = useState<AreaDraft | null>(null)
  const [areaName, setAreaName] = useState('')
  const [areaError, setAreaError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const { areas: next } = await knowledgeApi.listAreas(projectId)
      setAreas(next)
      onAreasChanged?.(next)
      setError(null)
      // Expand every parent on first load so the whole tree is visible; later
      // loads keep whatever the user has collapsed.
      if (!initializedRef.current) {
        initializedRef.current = true
        setExpanded(
          new Set(next.filter((a) => next.some((c) => c.parentId === a.id)).map((a) => a.id))
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load modules')
    }
  }, [projectId, onAreasChanged])

  useEffect(() => {
    void load()
  }, [load])

  // While any area has a consolidation in flight, refresh the list so the phase
  // badge advances and flips to ready/failed when it finishes.
  const anyConsolidating = (areas ?? []).some((a) => a.consolidation)
  useEffect(() => {
    if (!anyConsolidating) return
    const timer = setInterval(() => void load(), 3000)
    return () => clearInterval(timer)
  }, [anyConsolidating, load])

  const tree = useMemo(() => (areas ? buildTree(areas) : []), [areas])
  const rows = useMemo(() => flattenVisible(tree, expanded), [tree, expanded])

  // Ensure the selected area is visible after refresh (e.g. ?areaId=12 deep in tree).
  useEffect(() => {
    if (!areas || selectedAreaId == null) return
    const selected = areas.find((a) => a.id === selectedAreaId)
    if (!selected) return
    const ids = selected.path
      .split('/')
      .filter(Boolean)
      .map(Number)
      .filter((n) => !Number.isNaN(n))
    const ancestors = ids.filter((id) => id !== selectedAreaId)
    if (ancestors.length === 0) return
    setExpanded((prev) => {
      const next = new Set(prev)
      let changed = false
      for (const id of ancestors) {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [areas, selectedAreaId])

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      try {
        await fn()
        await load()
        return true
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Operation failed')
        return false
      } finally {
        setBusy(false)
        setPending(null)
      }
    },
    [load]
  )

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  const addArea = (parentId: number | null) => {
    const parentName =
      parentId == null ? null : (areas?.find((a) => a.id === parentId)?.name ?? null)
    setAreaDraft({ parentId, parentName })
    setAreaName('')
    setAreaError(null)
  }

  const closeAreaDialog = () => {
    setAreaDraft(null)
    setAreaName('')
    setAreaError(null)
  }

  const submitAreaDialog = async () => {
    if (!areaDraft) return
    const name = areaName.trim()
    if (!name) {
      setAreaError('Enter a name.')
      return
    }

    const { parentId } = areaDraft
    const areaType = parentId == null ? 'DOMAIN' : 'MODULE'
    const created = await run(() =>
      knowledgeApi.createArea(projectId, { name, parentId, areaType })
    )
    if (created) {
      if (parentId != null) setExpanded((prev) => new Set(prev).add(parentId))
      closeAreaDialog()
    }
  }

  const rename = async (area: KnowledgeArea) => {
    const name = (
      await dialog.prompt({
        title: 'Rename area',
        label: 'Area name',
        value: area.name,
        confirmLabel: 'Rename area',
        validate: (value) => (value.trim() ? null : 'Enter an area name.'),
      })
    )?.trim()
    if (!name || name === area.name) return
    void run(() => knowledgeApi.updateArea(projectId, area.id, { name }))
  }

  const changeType = async (area: KnowledgeArea) => {
    const areaType = await dialog.select({
      title: 'Change area type',
      label: 'Area type',
      value: area.areaType,
      options: AREA_TYPES.map((type) => ({ label: type.replace(/_/g, ' '), value: type })),
      confirmLabel: 'Change type',
    })
    if (!areaType || areaType === area.areaType) return
    void run(() => knowledgeApi.updateArea(projectId, area.id, { areaType }))
  }

  const editAliases = async (area: KnowledgeArea) => {
    const raw = await dialog.prompt({
      title: 'Edit aliases',
      label: 'Aliases (comma-separated)',
      value: area.aliases.join(', '),
      confirmLabel: 'Save aliases',
    })
    if (raw == null) return
    const aliases = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    void run(() => knowledgeApi.updateArea(projectId, area.id, { aliases }))
  }

  const remove = async (area: KnowledgeArea) => {
    const confirmed = await dialog.confirm({
      title: 'Delete area?',
      description: `Delete "${area.name}" and everything under it? This cannot be undone.`,
      confirmLabel: 'Delete area',
      tone: 'danger',
    })
    if (!confirmed) return
    void run(() => knowledgeApi.deleteArea(projectId, area.id))
    if (selectedAreaId === area.id) onSelectArea(null)
  }

  const applyPending = (targetId: number) => {
    if (!pending) return
    if (pending.kind === 'move') {
      void run(() =>
        knowledgeApi.moveArea(projectId, pending.areaId, {
          newParentId: Number.isNaN(targetId) ? null : targetId,
        })
      )
    } else {
      void run(() => knowledgeApi.mergeArea(projectId, pending.areaId, targetId))
    }
  }

  const areaDialog = (
    <Modal
      isOpen={areaDraft !== null}
      onClose={closeAreaDialog}
      title={areaDraft?.parentId == null ? 'Add domain' : 'Add knowledge area'}
      size="md"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submitAreaDialog()
        }}
      >
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-harbor-text"
            htmlFor="knowledge-area-name"
          >
            Name
          </label>
          <input
            id="knowledge-area-name"
            autoFocus
            value={areaName}
            onChange={(e) => {
              setAreaName(e.target.value)
              if (areaError) setAreaError(null)
            }}
            placeholder={
              areaDraft?.parentId == null
                ? 'Finance, Procurement, Support...'
                : 'Policies, Playbooks, Datasets...'
            }
            className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-harbor-text placeholder-harbor-text-muted focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          {areaError && <p className="text-sm text-red-400">{areaError}</p>}
        </div>

        <div className="rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text-secondary">
          {areaDraft?.parentId == null ? (
            <p>Domains sit at the top level of the knowledge map.</p>
          ) : (
            <p>
              This area will live under{' '}
              <span className="text-harbor-text">{areaDraft.parentName}</span>.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeAreaDialog}
            className="rounded-lg border border-harbor-border px-4 py-2 text-sm font-medium text-harbor-text-secondary transition-colors hover:text-harbor-text"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )

  if (error && !areas) {
    return <div className="text-sm text-red-400">{error}</div>
  }
  if (!areas) {
    return <div className="text-sm text-harbor-text-muted">Loading modules…</div>
  }

  if (areas.length === 0) {
    return (
      <>
        <EmptyState
          icon="🗂️"
          title="No knowledge yet"
          description="Map this project into knowledge areas - domains, modules, datasets - so agents can navigate it from the top down."
          action={
            canManage ? (
              <button
                type="button"
                onClick={() => addArea(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Add a domain
              </button>
            ) : undefined
          }
        />
        {areaDialog}
      </>
    )
  }

  const moveTargets = pending
    ? areas.filter((a) => !subtreeIds(areas, pending.areaId).has(a.id))
    : []

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-harbor-text-muted">{areas.length} areas</span>
        {canManage && (
          <button
            type="button"
            onClick={() => void addArea(null)}
            className="text-xs font-medium text-emerald-400 hover:text-emerald-300"
          >
            + Add domain
          </button>
        )}
      </div>

      {error && <div className="text-xs text-red-400">{error}</div>}

      <ul className="text-sm">
        {rows.map(({ area, depth, hasChildren }) => (
          <li key={area.id}>
            <div
              className={`group flex items-center gap-1 rounded px-1 py-1 ${
                selectedAreaId === area.id
                  ? 'bg-harbor-surface-raised'
                  : 'hover:bg-harbor-surface-raised'
              }`}
              style={{ paddingLeft: `${depth * 16 + 4}px` }}
            >
              <button
                type="button"
                aria-label={hasChildren ? (expanded.has(area.id) ? 'Collapse' : 'Expand') : 'Leaf'}
                onClick={() => hasChildren && toggle(area.id)}
                className="w-4 shrink-0 text-harbor-text-muted"
              >
                {hasChildren ? (expanded.has(area.id) ? '▾' : '▸') : '·'}
              </button>
              <button
                type="button"
                onClick={() => onSelectArea(area.id)}
                className="flex-1 text-left truncate text-harbor-text"
              >
                <span className="mr-2 rounded bg-harbor-border px-1 text-[10px] uppercase text-harbor-text-secondary">
                  {area.areaType}
                </span>
                {area.name}
                {area.status !== 'active' && (
                  <span className="ml-2 text-[10px] text-amber-400">{area.status}</span>
                )}
                <AreaBriefBadge area={area} />
              </button>
              {canManage && (
                <span className="hidden items-center gap-1 group-hover:flex">
                  <RowAction label="Add child" onClick={() => void addArea(area.id)} />
                  <RowAction label="Rename" onClick={() => void rename(area)} />
                  <RowAction label="Type" onClick={() => void changeType(area)} />
                  <RowAction label="Aliases" onClick={() => void editAliases(area)} />
                  <RowAction
                    label="Move"
                    onClick={() => setPending({ kind: 'move', areaId: area.id })}
                  />
                  <RowAction
                    label="Merge"
                    onClick={() => setPending({ kind: 'merge', areaId: area.id })}
                  />
                  <RowAction label="Delete" onClick={() => void remove(area)} danger />
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {pending && (
        <div className="rounded-lg border border-harbor-border bg-harbor-surface p-3 text-sm">
          <label className="block text-xs text-harbor-text-secondary mb-1">
            {pending.kind === 'move' ? 'Move under…' : 'Merge into…'}
          </label>
          <div className="flex items-center gap-2">
            <select
              aria-label={pending.kind === 'move' ? 'Move target' : 'Merge target'}
              disabled={busy}
              defaultValue=""
              onChange={(e) => e.target.value && applyPending(Number(e.target.value))}
              className="flex-1 rounded border border-harbor-border bg-harbor-surface-raised px-2 py-1 text-harbor-text"
            >
              <option value="" disabled>
                Select an area…
              </option>
              {pending.kind === 'move' && <option value={String(Number.NaN)}>(top level)</option>}
              {moveTargets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPending(null)}
              className="text-xs text-harbor-text-muted hover:text-harbor-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {areaDialog}
    </div>
  )
}

function AreaBriefBadge({ area }: { area: KnowledgeArea }) {
  if (area.consolidation) {
    return (
      <span
        className="ml-2 inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1 text-[10px] text-emerald-300"
        title={`Consolidating — ${consolidationPhaseLabel(area.consolidation.phase)}`}
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
        {consolidationPhaseLabel(area.consolidation.phase)}
      </span>
    )
  }
  if (area.briefStatus === 'ready') {
    return <span className="ml-2 text-[10px] text-harbor-text-muted">brief ✓</span>
  }
  if (area.briefStatus === 'stale') {
    return <span className="ml-2 text-[10px] text-amber-400">brief stale</span>
  }
  if (area.briefStatus === 'failed') {
    return <span className="ml-2 text-[10px] text-red-400">brief failed</span>
  }
  return null
}

function RowAction({
  label,
  onClick,
  danger,
}: {
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-1 text-[10px] ${
        danger ? 'text-red-400 hover:text-red-300' : 'text-harbor-text-muted hover:text-harbor-text'
      }`}
    >
      {label}
    </button>
  )
}
